import React from 'react';
import { useGetAllActiveMerchantsIdAndNameCachedQuery, useGetAllActiveMerchantsIdAndNameQuery, useGetAllActiveMerchantsIdCached2Query, useGetAllCourierPartnersCachedQuery } from '../redux/commonApiCalls';
import _ from "lodash"
import { RHFSearchableSelectField, RHFSelectField, RHFMultipleSelectField } from "./hook-form"
import { useState, useEffect } from 'react';
import { apiConstants, post } from './constants';


export function MerchantFilter() {
    // const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameQuery()
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    const activeMerchant = merchants.filter((val) => val.is_deleted === "0")
    return (
        <RHFSelectField
            width="100%"
            name="merchant"
            label="Select Merchant"
            options={_.orderBy(activeMerchant, "name").map((val) => ({
                value: val.id,
                label: val.name
            }))}
        />
    )
}

// `multiple` (opt-in): pick several merchants; the field value is comma-separated ids ("id1,id2").
export function MerchantFilterCached({ required = false, merchantType = null, fullWidth = true, placeholder = 'Select Merchant', multiple = false, selectAll = false }) {
    const { data: merchants = [], isLoading, isFetching } = useGetAllActiveMerchantsIdAndNameCachedQuery()
    let activeMerchant = merchants.filter((val) => val.is_deleted === "0")
    if (merchantType) {
        activeMerchant = activeMerchant.filter((val) => val[merchantType] === true)
    }
    // Only a "loading" state while we genuinely have nothing to show. A background
    // refetch over an already-populated cache must not blank the list into a spinner.
    const isLoadingOptions = (isLoading || isFetching) && activeMerchant.length === 0
    const options = _.orderBy(activeMerchant, "name").map((val) => ({
        value: val.id,
        label: val.companyName ? `${val.name} (${val.companyName})` : val.name
    }))
    if (multiple) {
        return (
            <RHFMultipleSelectField
                name="merchant"
                label="Select Merchant"
                placeholder={placeholder}
                width="100%"
                search
                outputFormat="string"
                selectAll={selectAll}
                noOptionsText={isLoadingOptions ? 'Loading merchants…' : 'No options available'}
                options={options}
            />
        )
    }
    return (
        <RHFSearchableSelectField
            // width="100%"/
            fullWidth={fullWidth}
            name="merchant"
            label="Select Merchant"
            placeholder={placeholder}
            required={required}
            loading={isLoadingOptions}
            loadingText="Loading merchants…"
            options={options}
        />
    )
}

export function AllMerchantFilter({ fullWidth = true }) {
    // const { data: merchants = [] } = useGetAllMerchantsIdAndNameQuery()
    const { data: merchants = [] } = useGetAllActiveMerchantsIdCached2Query()
    const activeMerchant = merchants.filter((val) => val.is_deleted === "0")

    return (
        <RHFSearchableSelectField fullWidth={fullWidth} width="100%" name="merchant" label={`Select Merchant`} options={_.orderBy(activeMerchant, "name").map((val) => ({
            value: val.id,
            label: val.name
        }))} />
    )
}

// Sentinel value for the chained Courier (parent) → Service Type → Account
// Code filter on Forward / Reverse Shipments. Selecting "All Service Types"
// in the Service Type multi-select means "do not narrow by service type" —
// the consuming page treats this as no service-type clause and lets every
// account code under the chosen parent through. Mirrors the same pattern in
// the Bulk Serviceability wizard.
export const ALL_SERVICE_TYPES = '__ALL_SERVICE_TYPES__';
export const ALL_SERVICE_TYPES_OPTION = { label: 'All Service Types', value: ALL_SERVICE_TYPES };

// Shared cache-key payload for the courier-partners list. Any other hook that
// uses `useGetAllCourierPartnersCachedQuery` and needs to share the cache with
// CourierFilter MUST import and pass this exact object (referential equality
// matters for hook stability; same shape matters for RTK Query's cache key).
// serviceTypePrimary + serviceTypeSecondary are included so pages adding a
// "Service Type" filter beside Courier don't have to issue a duplicate fetch.
export const COURIER_LIST_QUERY_PAYLOAD = {
    "andfilter": [{ "field": "is_deleted", "operator": "eq", "value": '0' }],
    "offset": 0,
    "limit": 1000,
    "skipCount": true,
    "includes": "id,name,parent,cpAccountCode,prozoId,supportedShipmentType,isBlended,type,serviceTypePrimary,serviceTypeSecondary,logicalName"
};

// `multiple` (opt-in): pick several couriers; the field value is comma-separated ids ("id1,id2").
export function CourierFilter({ filterList = null, type = null, fullWidth = true, placeholder = 'Select Courier', label = 'Select Courier', multiple = false }) {
    const { data } = useGetAllCourierPartnersCachedQuery(COURIER_LIST_QUERY_PAYLOAD);
    const filteredData = data?.filter(val => {
        if (filterList && type) {
            return val.supported_shipment === filterList && val.type === type;
        }
        if (filterList) {
            return val.supported_shipment === filterList;
        }
        if (type) {
            return val.type === type;
        }
        return true;
    });

    // Label stays the short account code + Prozo ID. The derived logical name (Courier Code Display,
    // e.g. DELHIVERY_FWD_SURFACE_REG_0.5KG) is a muted second line in the dropdown and is searchable,
    // but never part of the label — it is too long for the input and not yet the selection key.
    const options = _.orderBy(filteredData, 'name').map(val => ({
        value: val.id,
        label: val.cpAccountCode + " (" + val.prozoId + ")",
        secondary: val.logicalName || null,
        additionalData: val.prozoId
    }));
    if (multiple) {
        return (
            <RHFMultipleSelectField
                name="courier"
                label={label}
                placeholder={placeholder}
                width="100%"
                search
                outputFormat="string"
                options={options}
            />
        );
    }
    return (
        <>
            <RHFSearchableSelectField
                fullWidth={fullWidth}
                name="courier"
                label={label}
                placeholder={placeholder}
                options={options}
            />
        </>
    );
}


export function MerchantList({ onMerchantSelect }) {
    const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameQuery()
    const [selectedMerchant, setSelectedMerchant] = useState('');
    const handleMerchantChange = (selectedValue) => {
        setSelectedMerchant(selectedValue);
        onMerchantSelect(selectedValue);
    };
    const options = _.orderBy(merchants, 'name').map((val) => ({
        value: val.id,
        label: val.name.length > 20 ? `${val.name.substring(0, 20)}...` : val.name,
    }));
    return (
        <RHFSelectField
            width="100%"
            sx={{
                overflow: 'hidden',
            }}
            name="merchant"
            label="Select Merchant"
            options={options}
            value={selectedMerchant?.target?.value}
            onChange={handleMerchantChange}
        />
    )
}

export function CourierFilterParent() {
    const andfilter = [{ "field": "is_deleted", "operator": "eq", "value": '0' }];
    const filter = {
        "andfilter": andfilter,
        "offset": 0,
        "limit": 1000,
        // "controllerFilter": true,
        "skipCount": true,
        "includes": "id,name,cpAccountCode,prozoId,supportedShipmentType,isBlended,parent"
        // "excludes": exclude,
    }
    const { data } = useGetAllCourierPartnersCachedQuery(filter);
    return (
        <>
            <RHFSelectField
                width="100%"
                name="courier"
                label={`Select Courier`}
                options={_.orderBy(data, 'name').map(val => ({
                    value: val.parent,
                    label: val.parent,
                }))}
            />
        </>
    );
}

// Candidates are scoped to internal staff who actually hold escalation access —
// either the specific ESCALATION_CREATE/ESCALATION_ADMIN modules, or full super-admin
// (ROLE_PROSHIPADMIN, matching EscalationController.checkAccess()'s isProshipAdmin()
// bypass branch, which grants unrestricted Escalation Desk access without either
// module being explicitly listed). Assigning a ticket to someone who can't open the
// Escalation Desk would be a dead end. Fetched once per mount; this list is small
// (internal staff only) so a single unpaginated fetch is enough, unlike the
// merchant/courier pickers above which cache via RTK Query.
// `multiple`: renders a checkbox multi-select (array of ids) instead of a single-value
// autocomplete — use this wherever the config actually accepts more than one candidate
// (e.g. escalationAutoTagConfig.assignedUserIds, which load-balances across every entry).
export function InternalUserPicker({ name = 'assignedUserId', label = 'Assign To', fullWidth = true, placeholder = 'Select internal user', multiple = false }) {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setLoading(true);
        post(apiConstants.GET_USER, {
            collection: 'users',
            orfilter: [
                { field: 'accessList', operator: 'in', value: ['ESCALATION_CREATE', 'ESCALATION_ADMIN'] },
                { field: 'roles', operator: 'in', value: ['ROLE_PROSHIPADMIN'] },
            ],
            offset: 0,
            limit: 500,
            skipCount: true,
        }).then((res) => {
            setUsers(res?.data?.data || []);
        }).catch((err) => console.log(err)).finally(() => setLoading(false));
    }, []);

    const options = _.orderBy(users, 'fullName').map((u) => ({
        value: u.id,
        label: u.email ? `${u.fullName || u.username} (${u.email})` : (u.fullName || u.username),
    }));

    if (multiple) {
        return (
            <RHFMultipleSelectField
                name={name}
                label={label}
                width="100%"
                fullWidth
                outputFormat="array"
                noOptionsText={loading ? 'Loading users…' : 'No options available'}
                options={options}
            />
        );
    }

    return (
        <RHFSearchableSelectField
            fullWidth={fullWidth}
            name={name}
            label={label}
            placeholder={placeholder}
            loading={loading}
            loadingText="Loading users…"
            options={options}
        />
    );
}